import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeSearchQuery } from '../utils/sanitizeSearchQuery';
import './TournamentCreate.css';

/* ─────────────────────────────────────────────────────────────
   TOURNAMENT CREATION WIZARD

   Step 0: Tournament Type    (Single Elimination | Round Robin)
   Step 1: Name + Settings    (format, seeding [SE only], ELO toggle, Singles/Doubles)
   Step 2: Add Players/Teams  (friends first, then search; teams for doubles)
   Step 3: Review + Create    (summary → RPC)
   ───────────────────────────────────────────────────────────── */
function TournamentCreate() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const [step, setStep] = useState(0);

  // ── Type selection ─────────────────────────────────────────
  const [tournamentType, setTournamentType] = useState(''); // 'single_elimination' | 'round_robin'

  // ── Step 1 state ───────────────────────────────────────────
  const [name,        setName]        = useState('');
  const [playerCount, setPlayerCount] = useState(8);
  const [format,      setFormat]      = useState('bo3');
  const [seeding,     setSeeding]     = useState('elo');
  const [countsElo,   setCountsElo]   = useState(true);
  const [isDoubles,   setIsDoubles]   = useState(false);

  // ── Singles state (Step 2) ─────────────────────────────────
  const [creatorProfile,   setCreatorProfile]   = useState(null);
  const [friends,          setFriends]          = useState([]);
  const [selectedPlayers,  setSelectedPlayers]  = useState([]);
  const [searchQuery,      setSearchQuery]      = useState('');
  const [searchResults,    setSearchResults]    = useState([]);
  const [searchLoading,    setSearchLoading]    = useState(false);

  // ── Doubles state (Step 2) ─────────────────────────────────
  const [selectedTeams, setSelectedTeams] = useState([]);  // [{player1, player2}]
  const [addingTeam,    setAddingTeam]    = useState(false);
  const [draftP1,       setDraftP1]       = useState(null);
  const [draftP2,       setDraftP2]       = useState(null);
  const [p1Query,       setP1Query]       = useState('');
  const [p2Query,       setP2Query]       = useState('');
  const [p1Results,     setP1Results]     = useState([]);
  const [p2Results,     setP2Results]     = useState([]);
  const [p1Loading,     setP1Loading]     = useState(false);
  const [p2Loading,     setP2Loading]     = useState(false);

  // ── Submit state ───────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');

  // ── Load creator profile + friends on mount ────────────────
  useEffect(() => {
    if (!user) return;
    loadCreatorAndFriends();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadCreatorAndFriends() {
    const { data: me } = await supabase
      .from('profiles')
      .select('id, username, elo_rating, doubles_elo')
      .eq('id', user.id)
      .single();
    if (me) {
      setCreatorProfile(me);
      setSelectedPlayers([me]);
    }
    loadFriends();
  }

  async function loadFriends() {
    const [{ data: asSender }, { data: asReceiver }] = await Promise.all([
      supabase.from('friendships').select('friend_id').eq('user_id', user.id).eq('status', 'accepted'),
      supabase.from('friendships').select('user_id').eq('friend_id', user.id).eq('status', 'accepted'),
    ]);
    const ids = [
      ...(asSender   || []).map(r => r.friend_id),
      ...(asReceiver || []).map(r => r.user_id),
    ];
    if (!ids.length) return;
    const { data: profiles } = await supabase
      .from('profiles').select('id, username, elo_rating, doubles_elo').in('id', ids);
    setFriends(profiles || []);
  }

  // ── Debounced singles player search ───────────────────────
  useEffect(() => {
    if (searchQuery.trim().length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, username, elo_rating')
        .or(`full_name.ilike.%${sanitizeSearchQuery(searchQuery.trim())}%,username.ilike.%${sanitizeSearchQuery(searchQuery.trim())}%`)
        .neq('id', user.id)
        .limit(6);
      const selectedIds = selectedPlayers.map(p => p.id);
      setSearchResults((data || []).filter(p => !selectedIds.includes(p.id)));
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedPlayers, user]);

  // ── Debounced doubles team player searches ─────────────────
  const teamPlayerIds = selectedTeams.flatMap(t => [t.player1.id, t.player2.id]);

  useEffect(() => {
    if (!isDoubles || !addingTeam || p1Query.trim().length < 2) { setP1Results([]); return; }
    setP1Loading(true);
    const excluded = new Set([...teamPlayerIds, draftP2?.id].filter(Boolean));
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, username, elo_rating')
        .or(`full_name.ilike.%${sanitizeSearchQuery(p1Query.trim())}%,username.ilike.%${sanitizeSearchQuery(p1Query.trim())}%`)
        .limit(8);
      setP1Results((data || []).filter(p => !excluded.has(p.id)));
      setP1Loading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [p1Query, selectedTeams, draftP2, isDoubles, addingTeam]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isDoubles || !addingTeam || p2Query.trim().length < 2) { setP2Results([]); return; }
    setP2Loading(true);
    const excluded = new Set([...teamPlayerIds, draftP1?.id].filter(Boolean));
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, username, elo_rating')
        .or(`full_name.ilike.%${sanitizeSearchQuery(p2Query.trim())}%,username.ilike.%${sanitizeSearchQuery(p2Query.trim())}%`)
        .limit(8);
      setP2Results((data || []).filter(p => !excluded.has(p.id)));
      setP2Loading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [p2Query, selectedTeams, draftP1, isDoubles, addingTeam]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Singles player helpers ─────────────────────────────────
  function addPlayer(player) {
    if (selectedPlayers.find(p => p.id === player.id)) return;
    const cap = tournamentType === 'round_robin' ? 16 : playerCount;
    if (selectedPlayers.length >= cap) return;
    setSelectedPlayers(prev => [...prev, player]);
    setSearchQuery('');
    setSearchResults([]);
  }

  function removePlayer(playerId) {
    setSelectedPlayers(prev => prev.filter(p => p.id !== playerId));
  }

  // ── Doubles mode toggle ────────────────────────────────────
  function switchDoublesMode(val) {
    setIsDoubles(val);
    setSelectedTeams([]);
    setAddingTeam(false);
    setDraftP1(null); setDraftP2(null);
    setP1Query(''); setP2Query('');
    setP1Results([]); setP2Results([]);
    if (!val && creatorProfile) {
      setSelectedPlayers([creatorProfile]);
    }
    // Reset player count if 16 is selected (not supported for doubles SE)
    if (val && playerCount === 16) setPlayerCount(8);
  }

  // ── Doubles team helpers ───────────────────────────────────
  function confirmTeam() {
    if (!draftP1 || !draftP2) return;
    setSelectedTeams(prev => [...prev, { player1: draftP1, player2: draftP2 }]);
    setDraftP1(null); setDraftP2(null);
    setP1Query(''); setP2Query('');
    setP1Results([]); setP2Results([]);
    setAddingTeam(false);
  }

  function removeTeam(idx) {
    setSelectedTeams(prev => prev.filter((_, i) => i !== idx));
  }

  function cancelAddTeam() {
    setAddingTeam(false);
    setDraftP1(null); setDraftP2(null);
    setP1Query(''); setP2Query('');
    setP1Results([]); setP2Results([]);
  }

  // ── Step validation ────────────────────────────────────────
  function step1Valid() {
    return name.trim().length >= 2;
  }

  function step2Valid() {
    if (isDoubles) {
      const n = selectedTeams.length;
      if (tournamentType === 'round_robin') return n >= 3 && n <= 8;
      return n === playerCount;
    }
    if (tournamentType === 'round_robin') {
      return selectedPlayers.length >= 3 && selectedPlayers.length <= 16;
    }
    return selectedPlayers.length === playerCount;
  }

  // ── Final submit ───────────────────────────────────────────
  async function handleCreate() {
    setSubmitting(true);
    setError('');

    let data, rpcErr;

    if (isDoubles) {
      const p1Ids = selectedTeams.map(t => t.player1.id);
      const p2Ids = selectedTeams.map(t => t.player2.id);
      if (tournamentType === 'round_robin') {
        ({ data, error: rpcErr } = await supabase.rpc('create_doubles_rr_tournament', {
          p_name:               name.trim(),
          p_format:             format,
          p_counts_towards_elo: countsElo,
          p_team_p1_ids:        p1Ids,
          p_team_p2_ids:        p2Ids,
        }));
      } else {
        ({ data, error: rpcErr } = await supabase.rpc('create_doubles_tournament', {
          p_name:               name.trim(),
          p_team_count:         playerCount,
          p_seeding:            seeding,
          p_format:             format,
          p_counts_towards_elo: countsElo,
          p_team_p1_ids:        p1Ids,
          p_team_p2_ids:        p2Ids,
        }));
      }
    } else if (tournamentType === 'round_robin') {
      ({ data, error: rpcErr } = await supabase.rpc('create_round_robin_tournament', {
        p_name:               name.trim(),
        p_format:             format,
        p_counts_towards_elo: countsElo,
        p_player_ids:         selectedPlayers.map(p => p.id),
      }));
    } else {
      ({ data, error: rpcErr } = await supabase.rpc('create_tournament', {
        p_name:               name.trim(),
        p_player_count:       playerCount,
        p_seeding:            seeding,
        p_format:             format,
        p_counts_towards_elo: countsElo,
        p_player_ids:         selectedPlayers.map(p => p.id),
      }));
    }

    setSubmitting(false);

    if (rpcErr || data?.error) {
      setError(rpcErr?.message || data?.error || 'Failed to create tournament.');
      return;
    }

    navigate(`/tournaments/${data.tournament_id}`);
  }

  // ── Derived ────────────────────────────────────────────────
  const doublesTeamCap     = tournamentType === 'round_robin' ? 8 : playerCount;
  const cap                = isDoubles ? doublesTeamCap : (tournamentType === 'round_robin' ? 16 : playerCount);
  const slotsLeft          = isDoubles
    ? doublesTeamCap - selectedTeams.length
    : cap - selectedPlayers.length;
  const friendsNotSelected = friends.filter(f => !selectedPlayers.find(p => p.id === f.id));
  const friendsNotInTeams  = friends.filter(f => !teamPlayerIds.includes(f.id));
  const formatLabels       = { bo1: 'Best of 1', bo3: 'Best of 3', bo5: 'Best of 5', bo7: 'Best of 7' };

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="tc-page">
      <div className="tc-container">

        {/* Header */}
        <div className="tc-header">
          <Link to="/tournaments" className="tc-back">← Tournaments</Link>
          <h1 className="tc-title">Create Tournament</h1>
        </div>

        {/* Step indicator — only show from step 1 onward */}
        {step >= 1 && (
          <div className="tc-steps">
            {[1, 2, 3].map(s => (
              <div key={s} className={`tc-step ${s === step ? 'active' : s < step ? 'done' : ''}`}>
                <div className="tc-step-dot">{s < step ? '✓' : s}</div>
                <span className="tc-step-label">
                  {s === 1 ? 'Settings' : s === 2 ? (isDoubles ? 'Teams' : 'Players') : 'Review'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ── Step 0: Tournament Type ───────────────────────── */}
        {step === 0 && (
          <div className="tc-card">
            <p className="tc-label">Choose Tournament Type</p>
            <div className="tc-type-cards">

              <button
                className="tc-type-card"
                onClick={() => { setTournamentType('single_elimination'); setStep(1); }}
              >
                <span className="tc-type-icon">🏆</span>
                <strong className="tc-type-title">Single Elimination</strong>
                <span className="tc-type-desc">
                  Players are eliminated after one loss. 4 or 8 players (or teams).
                  Classic bracket — best players meet in the later rounds.
                </span>
              </button>

              <button
                className="tc-type-card"
                onClick={() => { setTournamentType('round_robin'); setStep(1); }}
              >
                <span className="tc-type-icon">🔄</span>
                <strong className="tc-type-title">Round Robin</strong>
                <span className="tc-type-desc">
                  Everyone plays everyone. 3–16 players (or 3–8 teams). Winner
                  determined by most match wins.
                </span>
              </button>

            </div>
          </div>
        )}

        {/* ── Step 1: Name + Settings ───────────────────────── */}
        {step === 1 && (
          <div className="tc-card">
            <div className="tc-field">
              <label className="tc-label">Tournament Name</label>
              <input
                className="tc-input"
                placeholder="e.g. Office Championship 2026"
                value={name}
                onChange={e => setName(e.target.value)}
                maxLength={60}
              />
            </div>

            {/* Singles / Doubles toggle */}
            <div className="tc-field">
              <label className="tc-label">Match Type</label>
              <div className="tc-match-type-toggle">
                <button
                  className={`tc-match-type-btn ${!isDoubles ? 'active' : ''}`}
                  onClick={() => switchDoublesMode(false)}
                >
                  Singles
                </button>
                <button
                  className={`tc-match-type-btn ${isDoubles ? 'active' : ''}`}
                  onClick={() => switchDoublesMode(true)}
                >
                  Doubles (2v2)
                </button>
              </div>
            </div>

            {/* Team / Player count — Single Elimination only */}
            {tournamentType === 'single_elimination' && (
              <div className="tc-field">
                <label className="tc-label">{isDoubles ? 'Number of Teams' : 'Number of Players'}</label>
                <div className="tc-options">
                  {(isDoubles ? [4, 8] : [4, 8, 16]).map(n => (
                    <button
                      key={n}
                      className={`tc-option ${playerCount === n ? 'active' : ''}`}
                      onClick={() => {
                        setPlayerCount(n);
                        if (isDoubles) setSelectedTeams([]);
                        else setSelectedPlayers(prev => prev.filter(p => p.id === user.id));
                      }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="tc-field">
              <label className="tc-label">Match Format</label>
              <div className="tc-options">
                {['bo1', 'bo3', 'bo5', 'bo7'].map(f => (
                  <button
                    key={f}
                    className={`tc-option ${format === f ? 'active' : ''}`}
                    onClick={() => setFormat(f)}
                  >
                    {formatLabels[f]}
                  </button>
                ))}
              </div>
            </div>

            {/* Seeding — Single Elimination only */}
            {tournamentType === 'single_elimination' && (
              <div className="tc-field">
                <label className="tc-label">Seeding</label>
                <div className="tc-options tc-options--col">
                  {['elo', 'random'].map(s => (
                    <button
                      key={s}
                      className={`tc-option tc-option--wide ${seeding === s ? 'active' : ''}`}
                      onClick={() => setSeeding(s)}
                    >
                      <strong>{s === 'elo' ? (isDoubles ? 'Doubles ELO Seeding' : 'ELO Seeding') : 'Random'}</strong>
                      <span>{s === 'elo'
                        ? 'Highest rated plays lowest — best players meet latest'
                        : (isDoubles ? 'Teams are placed randomly in the bracket' : 'Players are placed randomly in the bracket')}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="tc-field">
              <label className="tc-label">ELO Rating</label>
              <button
                className={`tc-toggle ${countsElo ? 'active' : ''}`}
                onClick={() => setCountsElo(prev => !prev)}
              >
                <div className="tc-toggle-track">
                  <div className="tc-toggle-thumb" />
                </div>
                <div className="tc-toggle-text">
                  <span>{countsElo
                    ? (isDoubles ? 'Counts towards Doubles ELO' : 'Counts towards ELO')
                    : 'Unranked (ELO not affected)'}</span>
                  <span className="tc-toggle-sub">
                    {countsElo
                      ? (isDoubles
                        ? 'Each match updates all 4 players\' doubles ratings as it is played'
                        : 'Each match updates both players\' global ratings as it is played')
                      : 'Results are recorded but ratings and reliability stay the same'}
                  </span>
                </div>
              </button>
            </div>

            <div className="tc-step-nav">
              <button className="tc-back-btn" onClick={() => setStep(0)}>← Back</button>
              <button
                className="tc-next-btn"
                disabled={!step1Valid()}
                onClick={() => setStep(2)}
              >
                {isDoubles ? 'Next: Add Teams →' : 'Next: Add Players →'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Add Players (Singles) ────────────────── */}
        {step === 2 && !isDoubles && (
          <div className="tc-card">
            <div className="tc-slots-bar">
              {tournamentType === 'round_robin' ? (
                <span className="tc-slots-label">
                  {selectedPlayers.length} player{selectedPlayers.length !== 1 ? 's' : ''} added (3–16 required)
                </span>
              ) : (
                <>
                  <span className="tc-slots-label">
                    {selectedPlayers.length} / {playerCount} players added
                  </span>
                  <div className="tc-slots-track">
                    {Array.from({ length: playerCount }).map((_, i) => (
                      <div key={i} className={`tc-slot-pip ${i < selectedPlayers.length ? 'filled' : ''}`} />
                    ))}
                  </div>
                </>
              )}
            </div>

            {selectedPlayers.length > 0 && (
              <div className="tc-selected-list">
                {selectedPlayers.map(p => (
                  <div key={p.id} className="tc-selected-row">
                    <div className="tc-selected-avatar">{p.username[0].toUpperCase()}</div>
                    <div className="tc-selected-name-col">
                      <span className="tc-selected-name">
                        {p.username}
                        {p.id === user.id && <span className="tc-you-tag">You</span>}
                      </span>
                      <span className="tc-selected-elo">{p.elo_rating}</span>
                    </div>
                    {p.id !== user.id && (
                      <button className="tc-remove-btn" onClick={() => removePlayer(p.id)}>✕</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {slotsLeft > 0 && (
              <>
                <div className="tc-search-wrap">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    className="tc-search-input"
                    placeholder={
                      tournamentType === 'round_robin'
                        ? `Search players… (${slotsLeft} more can be added)`
                        : `Search players… (${slotsLeft} slot${slotsLeft !== 1 ? 's' : ''} left)`
                    }
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>

                {searchQuery.trim().length >= 2 && (
                  <div className="tc-search-results">
                    {searchLoading && <p className="tc-search-empty">Searching…</p>}
                    {!searchLoading && searchResults.length === 0 && (
                      <p className="tc-search-empty">No players found.</p>
                    )}
                    {searchResults.map(p => (
                      <div key={p.id} className="tc-search-row">
                        <div className="tc-search-avatar">{p.username[0].toUpperCase()}</div>
                        <div className="tc-search-name-col">
                          <span className="tc-search-name">{p.username}</span>
                          <span className="tc-search-elo">{p.elo_rating}</span>
                        </div>
                        <button className="tc-add-btn" onClick={() => addPlayer(p)}>+ Add</button>
                      </div>
                    ))}
                  </div>
                )}

                {!searchQuery && friendsNotSelected.length > 0 && (
                  <div className="tc-friends-section">
                    <p className="tc-friends-label">Your Friends</p>
                    {friendsNotSelected.map(f => (
                      <div key={f.id} className="tc-search-row">
                        <div className="tc-search-avatar">{f.username[0].toUpperCase()}</div>
                        <div className="tc-search-name-col">
                          <span className="tc-search-name">{f.username}</span>
                          <span className="tc-search-elo">{f.elo_rating}</span>
                        </div>
                        <button className="tc-add-btn" onClick={() => addPlayer(f)}>+ Add</button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="tc-step-nav">
              <button className="tc-back-btn" onClick={() => setStep(1)}>← Back</button>
              <button
                className="tc-next-btn"
                disabled={!step2Valid()}
                onClick={() => setStep(3)}
              >
                Next: Review →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Add Teams (Doubles) ───────────────────── */}
        {step === 2 && isDoubles && (
          <div className="tc-card">
            {/* Slots bar */}
            <div className="tc-slots-bar">
              {tournamentType === 'round_robin' ? (
                <span className="tc-slots-label">
                  {selectedTeams.length} team{selectedTeams.length !== 1 ? 's' : ''} added (3–8 required)
                </span>
              ) : (
                <>
                  <span className="tc-slots-label">
                    {selectedTeams.length} / {playerCount} teams added
                  </span>
                  <div className="tc-slots-track">
                    {Array.from({ length: playerCount }).map((_, i) => (
                      <div key={i} className={`tc-slot-pip ${i < selectedTeams.length ? 'filled' : ''}`} />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Confirmed teams list */}
            {selectedTeams.length > 0 && (
              <div className="tc-selected-list">
                {selectedTeams.map((team, i) => (
                  <div key={i} className="tc-selected-row">
                    <div className="tc-team-avatars">
                      <div className="tc-selected-avatar tc-team-avatar-sm">{team.player1.username[0].toUpperCase()}</div>
                      <div className="tc-selected-avatar tc-team-avatar-sm tc-team-avatar-offset">{team.player2.username[0].toUpperCase()}</div>
                    </div>
                    <div className="tc-selected-name-col">
                      <span className="tc-selected-name">{team.player1.username} &amp; {team.player2.username}</span>
                    </div>
                    <button className="tc-remove-btn" onClick={() => removeTeam(i)}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Team button / form */}
            {slotsLeft > 0 && !addingTeam && (
              <button className="tc-add-team-btn" onClick={() => setAddingTeam(true)}>
                + Add a Team
              </button>
            )}

            {addingTeam && (
              <div className="tc-team-form">
                {/* Player 1 */}
                <div className="tc-team-form-field">
                  <span className="tc-team-form-label">Player 1</span>
                  {draftP1 ? (
                    <div className="tc-selected-row tc-team-selected-slot">
                      <div className="tc-selected-avatar">{draftP1.username[0].toUpperCase()}</div>
                      <div className="tc-selected-name-col">
                        <span className="tc-selected-name">{draftP1.username}</span>
                        <span className="tc-selected-elo">{draftP1.elo_rating}</span>
                      </div>
                      <button className="tc-remove-btn" onClick={() => { setDraftP1(null); setP1Query(''); setP1Results([]); }}>✕</button>
                    </div>
                  ) : (
                    <>
                      <div className="tc-search-wrap">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                        <input
                          className="tc-search-input"
                          placeholder="Search for player 1…"
                          value={p1Query}
                          onChange={e => setP1Query(e.target.value)}
                          autoFocus
                        />
                      </div>
                      {p1Query.trim().length >= 2 && (
                        <div className="tc-search-results">
                          {p1Loading && <p className="tc-search-empty">Searching…</p>}
                          {!p1Loading && p1Results.length === 0 && <p className="tc-search-empty">No players found.</p>}
                          {p1Results.map(p => (
                            <div key={p.id} className="tc-search-row">
                              <div className="tc-search-avatar">{p.username[0].toUpperCase()}</div>
                              <div className="tc-search-name-col">
                                <span className="tc-search-name">{p.username}</span>
                                <span className="tc-search-elo">{p.elo_rating}</span>
                              </div>
                              <button className="tc-add-btn" onClick={() => { setDraftP1(p); setP1Query(''); setP1Results([]); }}>Select</button>
                            </div>
                          ))}
                        </div>
                      )}
                      {!p1Query && friendsNotInTeams.filter(f => f.id !== draftP2?.id).length > 0 && (
                        <div className="tc-friends-section">
                          <p className="tc-friends-label">Your Friends</p>
                          {friendsNotInTeams.filter(f => f.id !== draftP2?.id).map(f => (
                            <div key={f.id} className="tc-search-row">
                              <div className="tc-search-avatar">{f.username[0].toUpperCase()}</div>
                              <div className="tc-search-name-col">
                                <span className="tc-search-name">{f.username}</span>
                                <span className="tc-search-elo">{f.elo_rating}</span>
                              </div>
                              <button className="tc-add-btn" onClick={() => setDraftP1(f)}>Select</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Player 2 */}
                <div className="tc-team-form-field">
                  <span className="tc-team-form-label">Player 2</span>
                  {draftP2 ? (
                    <div className="tc-selected-row tc-team-selected-slot">
                      <div className="tc-selected-avatar">{draftP2.username[0].toUpperCase()}</div>
                      <div className="tc-selected-name-col">
                        <span className="tc-selected-name">{draftP2.username}</span>
                        <span className="tc-selected-elo">{draftP2.elo_rating}</span>
                      </div>
                      <button className="tc-remove-btn" onClick={() => { setDraftP2(null); setP2Query(''); setP2Results([]); }}>✕</button>
                    </div>
                  ) : (
                    <>
                      <div className="tc-search-wrap">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                        <input
                          className="tc-search-input"
                          placeholder="Search for player 2…"
                          value={p2Query}
                          onChange={e => setP2Query(e.target.value)}
                        />
                      </div>
                      {p2Query.trim().length >= 2 && (
                        <div className="tc-search-results">
                          {p2Loading && <p className="tc-search-empty">Searching…</p>}
                          {!p2Loading && p2Results.length === 0 && <p className="tc-search-empty">No players found.</p>}
                          {p2Results.map(p => (
                            <div key={p.id} className="tc-search-row">
                              <div className="tc-search-avatar">{p.username[0].toUpperCase()}</div>
                              <div className="tc-search-name-col">
                                <span className="tc-search-name">{p.username}</span>
                                <span className="tc-search-elo">{p.elo_rating}</span>
                              </div>
                              <button className="tc-add-btn" onClick={() => { setDraftP2(p); setP2Query(''); setP2Results([]); }}>Select</button>
                            </div>
                          ))}
                        </div>
                      )}
                      {!p2Query && friendsNotInTeams.filter(f => f.id !== draftP1?.id).length > 0 && (
                        <div className="tc-friends-section">
                          <p className="tc-friends-label">Your Friends</p>
                          {friendsNotInTeams.filter(f => f.id !== draftP1?.id).map(f => (
                            <div key={f.id} className="tc-search-row">
                              <div className="tc-search-avatar">{f.username[0].toUpperCase()}</div>
                              <div className="tc-search-name-col">
                                <span className="tc-search-name">{f.username}</span>
                                <span className="tc-search-elo">{f.elo_rating}</span>
                              </div>
                              <button className="tc-add-btn" onClick={() => setDraftP2(f)}>Select</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Confirm / Cancel */}
                <div className="tc-team-form-actions">
                  <button className="tc-back-btn" onClick={cancelAddTeam}>Cancel</button>
                  <button
                    className="tc-next-btn"
                    disabled={!draftP1 || !draftP2}
                    onClick={confirmTeam}
                  >
                    Confirm Team
                  </button>
                </div>
              </div>
            )}

            <div className="tc-step-nav">
              <button className="tc-back-btn" onClick={() => setStep(1)}>← Back</button>
              <button
                className="tc-next-btn"
                disabled={!step2Valid()}
                onClick={() => setStep(3)}
              >
                Next: Review →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Review + Create ───────────────────────── */}
        {step === 3 && (
          <div className="tc-card">
            <div className="tc-review-section">
              <p className="tc-review-label">Tournament</p>
              <p className="tc-review-value">{name}</p>
            </div>

            <div className="tc-review-grid">
              <div className="tc-review-item">
                <span className="tc-review-item-label">{isDoubles ? 'Teams' : 'Players'}</span>
                <span className="tc-review-item-value">
                  {isDoubles
                    ? selectedTeams.length
                    : (tournamentType === 'round_robin' ? selectedPlayers.length : playerCount)}
                </span>
              </div>
              <div className="tc-review-item">
                <span className="tc-review-item-label">Format</span>
                <span className="tc-review-item-value">{formatLabels[format]}</span>
              </div>
              {tournamentType === 'single_elimination' ? (
                <div className="tc-review-item">
                  <span className="tc-review-item-label">Seeding</span>
                  <span className="tc-review-item-value">{seeding === 'elo' ? 'ELO' : 'Random'}</span>
                </div>
              ) : (
                <div className="tc-review-item">
                  <span className="tc-review-item-label">Type</span>
                  <span className="tc-review-item-value">Round Robin</span>
                </div>
              )}
              <div className="tc-review-item">
                <span className="tc-review-item-label">ELO</span>
                <span className="tc-review-item-value">{countsElo ? 'Ranked' : 'Unranked'}</span>
              </div>
              <div className="tc-review-item">
                <span className="tc-review-item-label">Match Type</span>
                <span className="tc-review-item-value">{isDoubles ? '2v2 Doubles' : 'Singles'}</span>
              </div>
            </div>

            {/* Players / Teams list */}
            <div className="tc-review-section">
              {isDoubles ? (
                <>
                  <p className="tc-review-label">Teams ({selectedTeams.length})</p>
                  <div className="tc-review-players">
                    {selectedTeams.map((team, i) => (
                      <div key={i} className="tc-review-player-row">
                        {tournamentType === 'single_elimination' && (
                          <span className="tc-review-seed">#{i + 1}</span>
                        )}
                        <div className="tc-team-avatars">
                          <div className="tc-search-avatar small">{team.player1.username[0].toUpperCase()}</div>
                          <div className="tc-search-avatar small tc-team-avatar-offset">{team.player2.username[0].toUpperCase()}</div>
                        </div>
                        <span className="tc-review-name">{team.player1.username} &amp; {team.player2.username}</span>
                      </div>
                    ))}
                  </div>
                  {tournamentType === 'single_elimination' && (
                    <p className="tc-review-hint">
                      {seeding === 'elo'
                        ? 'Teams will be seeded by average doubles ELO when the bracket is generated.'
                        : 'Teams will be seeded randomly when the bracket is generated.'}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="tc-review-label">Players ({selectedPlayers.length})</p>
                  <div className="tc-review-players">
                    {selectedPlayers.map((p, i) => (
                      <div key={p.id} className="tc-review-player-row">
                        {tournamentType === 'single_elimination' && (
                          <span className="tc-review-seed">#{i + 1}</span>
                        )}
                        <div className="tc-search-avatar small">{p.username[0].toUpperCase()}</div>
                        <span className="tc-review-name">{p.username}</span>
                        <span className="tc-review-elo">{p.elo_rating}</span>
                      </div>
                    ))}
                  </div>
                  {tournamentType === 'single_elimination' && seeding === 'elo' && (
                    <p className="tc-review-hint">
                      Seeds will be assigned by ELO when the bracket is generated.
                    </p>
                  )}
                  {tournamentType === 'single_elimination' && seeding === 'random' && (
                    <p className="tc-review-hint">
                      Seeds will be assigned randomly when the bracket is generated.
                    </p>
                  )}
                </>
              )}
            </div>

            {error && <p className="tc-error">{error}</p>}

            <div className="tc-step-nav">
              <button className="tc-back-btn" onClick={() => setStep(2)}>← Back</button>
              <button
                className="tc-next-btn"
                disabled={submitting}
                onClick={handleCreate}
              >
                {submitting ? 'Creating…' : '🏆 Create Tournament'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default TournamentCreate;
